// V1.1d — /admin/mailed-letters
//
// Operations view: every mailed letter across all users, with filters by
// status / lob mode / user email + a drawer to drill into tracking events
// + admin-only Refresh button.
//
// Wave 6 Batch 3 · Cluster A Turn 2 — chrome sweep onto `--ds-*` tokens.
// Anchor: CreditEngineEvents (Wave 5-Late).
//   • AdminPageHelp lifted out of `actions` (was mis-nested inside the
//     Refresh button JSX).
//   • `_primitives.Section` shells for filter + main table.
//   • `_primitives.SelectInput` / `TextInput` for filters.
//   • `_primitives.GhostButton` for Refresh / drawer refresh.
//   • STATUS_META pastel per-status color triplets REMOVED. Now a
//     STATUS_TONE map hits DS Badge sharp/soft tones. Icon retained
//     per-status.
//   • Dense hairline table `px-4 py-1.5` + `isLast` guard.
//   • Drawer chrome swept to `--ds-*`; backdrop `rgba(27,26,22,0.55)`.
//
// Every testid + API path + Lob field preserved BYTE-EXACT.

import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  PackageOpen,
  RefreshCw,
  Search,
  Send,
  Truck,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Badge } from "@/design-system/components/Badge";
import { Heading } from "@/design-system/components/Heading";

import { api, formatApiError } from "@/api/client";
import { Section, SelectInput, TextInput, GhostButton } from "@/admin/_primitives";

const STATUS_META = {
  draft:                  { label: "Draft",          icon: PackageOpen,  tone: "neutralMuted" },
  submitted:              { label: "Submitted",      icon: Send,         tone: "warning" },
  in_transit:             { label: "In transit",     icon: Truck,        tone: "accent" },
  processed_for_delivery: { label: "At post office", icon: Truck,        tone: "accent" },
  delivered:              { label: "Delivered",      icon: CheckCircle2, tone: "successMuted" },
  returned:               { label: "Returned",       icon: XCircle,      tone: "danger" },
  canceled:               { label: "Canceled",       icon: XCircle,      tone: "danger" },
  failed:                 { label: "Failed",         icon: XCircle,      tone: "danger" },
};

// Token color for the drawer timeline dot per status. Semantic map only —
// no hex literals.
const STATUS_DOT_COLOR = {
  draft:                  "var(--ds-color-ink-subtle)",
  submitted:              "var(--ds-color-warning)",
  in_transit:             "var(--ds-color-accent)",
  processed_for_delivery: "var(--ds-color-accent)",
  delivered:              "var(--ds-color-success-muted)",
  returned:               "var(--ds-color-danger)",
  canceled:               "var(--ds-color-danger)",
  failed:                 "var(--ds-color-danger)",
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.submitted;
  return (
    <Badge tone={meta.tone} size="sm" shape="sharp" className="uppercase tracking-[0.14em]">
      {meta.label}
    </Badge>
  );
}

export default function AdminMailedLetters() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filters, setFilters] = useState({ user_email: "", status: "", lob_mode: "" });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.status) params.status = filters.status;
      if (filters.lob_mode) params.lob_mode = filters.lob_mode;
      const { data } = await api.get("/admin/mailed-letters", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <AdminLayout
      title="Mailed Letters"
      subtitle="USPS letters mailed through Lob. Sandbox/stub mode simulates delivery over ~90 seconds."
      actions={
        <GhostButton data-testid="admin-mailed-letters-refresh" onClick={reload}>
          <RefreshCw className="w-3 h-3" /> Refresh
        </GhostButton>
      }
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Mailed Letters"
          purpose="Track physical mail dispatched via Lob — status, tracking number, delivery confirmation."
          configures="Nothing — read-only. Filter by member, carrier status, and time window."
          impact="No user-facing impact. Diagnose stuck mail or reconcile Lob billing."
          safety="Read-only. Lob is the source of truth for delivery status."
        />
      </div>

      <Section title="Filter" testid="admin-mailed-letters-filter-section">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[260px] relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
              style={{ color: "var(--ds-color-ink-subtle)" }}
              aria-hidden="true"
            />
            <TextInput
              data-testid="admin-mailed-letters-search"
              value={filters.user_email}
              onChange={(e) => setFilters((f) => ({ ...f, user_email: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
              placeholder="Search by user email…"
              className="w-full pl-9"
            />
          </div>
          <SelectInput
            data-testid="admin-mailed-letters-status-filter"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </SelectInput>
          <SelectInput
            data-testid="admin-mailed-letters-mode-filter"
            value={filters.lob_mode}
            onChange={(e) => setFilters((f) => ({ ...f, lob_mode: e.target.value }))}
          >
            <option value="">All modes</option>
            <option value="stub">Sandbox</option>
            <option value="live">Live</option>
          </SelectInput>
        </div>
      </Section>

      <Section
        title="Mailed letters"
        testid="admin-mailed-letters-section"
        bodyClassName="p-0"
        badge={
          <span
            className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            {items.length} showing
          </span>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr
                className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                style={{
                  color: "var(--ds-color-ink-subtle)",
                  background: "var(--ds-color-admin-surface-muted)",
                  borderBottom: "1px solid var(--ds-color-admin-border)",
                }}
              >
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Lob ID</th>
                <th className="px-4 py-2 hidden md:table-cell">Recipient</th>
                <th className="px-4 py-2 hidden md:table-cell">Status</th>
                <th className="px-4 py-2 hidden lg:table-cell">Mode</th>
                <th className="px-4 py-2 hidden lg:table-cell text-right">Price</th>
                <th className="px-4 py-2 hidden lg:table-cell">Submitted</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-6 text-center text-[13px]"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-6 text-center text-[13px]"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    No mailed letters match these filters.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const isLast = idx === items.length - 1;
                  return (
                    <tr
                      key={it.id}
                      data-testid={`admin-mailed-letters-row-${it.id}`}
                      className="cursor-pointer transition-colors"
                      onClick={() => setOpenId(it.id)}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        transitionDuration: "var(--ds-duration-fast)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        className="px-4 py-1.5 text-[12px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {it.user_email || it.user_id}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[11px] font-mono"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {it.lob_letter_id}
                      </td>
                      <td
                        className="px-4 py-1.5 hidden md:table-cell text-[12px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {it.to_address?.name || "—"} ({it.to_address?.state})
                      </td>
                      <td className="px-4 py-1.5 hidden md:table-cell">
                        <StatusPill status={it.status} />
                      </td>
                      <td
                        className="px-4 py-1.5 hidden lg:table-cell text-[12px] font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {it.lob_mode}
                      </td>
                      <td
                        className="px-4 py-1.5 hidden lg:table-cell text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        ${((it.price_cents || 0) / 100).toFixed(2)}
                      </td>
                      <td
                        className="px-4 py-1.5 hidden lg:table-cell text-[12px] tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {it.submitted_at ? new Date(it.submitted_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <button
                          data-testid={`admin-mailed-letters-open-${it.id}`}
                          onClick={(e) => { e.stopPropagation(); setOpenId(it.id); }}
                          className="text-[11px] uppercase tracking-[0.12em] font-semibold hover:underline"
                          style={{ color: "var(--ds-color-accent-strong)" }}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {openId && (
        <AdminMailedLetterDrawer
          mailedId={openId}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}
    </AdminLayout>
  );
}

function AdminMailedLetterDrawer({ mailedId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/mailed-letters/${mailedId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [mailedId, onClose]);

  useEffect(() => { load(); }, [load]);

  async function refresh() {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/mailed-letters/${mailedId}/refresh-status`);
      setDetail(data);
      toast.success("Status refreshed.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const dotColor = detail ? (STATUS_DOT_COLOR[detail.status] || STATUS_DOT_COLOR.submitted) : STATUS_DOT_COLOR.submitted;

  return (
    <div
      data-testid="admin-mailed-letters-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(27, 26, 22, 0.55)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl h-full overflow-y-auto"
        style={{
          background: "var(--ds-color-surface)",
          borderLeft: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="sticky top-0 flex items-center justify-between px-5 py-3.5 z-10 border-b"
          style={{
            background: "var(--ds-color-surface)",
            borderColor: "var(--ds-color-admin-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="admin-mailed-letters-drawer-back"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.12em] transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                Mailed letter
              </div>
              <Heading level={4} size="md" weight="semibold" className="truncate">
                {detail?.lob_letter_id || "…"}
              </Heading>
            </div>
          </div>
          <button
            data-testid="admin-mailed-letters-drawer-close"
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {loading || !detail ? (
          <div className="p-6 text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={detail.status} />
              <span className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                {detail.lob_mode === "stub" ? "Sandbox" : "Live"}
              </span>
              <div className="ml-auto">
                <GhostButton
                  type="button"
                  data-testid="admin-mailed-letters-drawer-refresh"
                  onClick={refresh}
                  disabled={busy}
                >
                  <RefreshCw className="w-3 h-3" /> Refresh status
                </GhostButton>
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-3 text-xs">
              <DetailField label="User ID" value={detail.user_id} />
              <DetailField label="Dispute letter ID" value={detail.dispute_letter_id} />
              <DetailField label="Recipient name" value={detail.to_address?.name || "—"} />
              <DetailField label="Recipient city/state" value={`${detail.to_address?.city || ""}, ${detail.to_address?.state || ""}`} />
              <DetailField label="Price" value={`$${((detail.price_cents || 0) / 100).toFixed(2)}`} />
              <DetailField label="Mail type" value={detail.options?.mail_type} />
              <DetailField label="Submitted" value={detail.submitted_at ? new Date(detail.submitted_at).toLocaleString() : "—"} />
              <DetailField label="Last sync" value={detail.last_synced_at ? new Date(detail.last_synced_at).toLocaleString() : "—"} />
              <DetailField label="ETA" value={detail.expected_delivery_date ? new Date(detail.expected_delivery_date).toLocaleString() : "—"} />
              {detail.actual_delivery_date && (
                <DetailField label="Delivered" value={new Date(detail.actual_delivery_date).toLocaleString()} />
              )}
            </dl>

            <div data-testid="admin-mailed-letters-drawer-timeline">
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Tracking timeline
              </div>
              <ol
                className="space-y-3 relative pl-5 border-l"
                style={{ borderColor: "var(--ds-color-admin-border)" }}
              >
                {(detail.tracking_events || []).map((ev, i) => (
                  <li key={i} className="relative">
                    <span
                      className="absolute -left-[26px] top-1 w-3 h-3 rounded-full"
                      style={{ background: dotColor }}
                    />
                    <div className="text-sm" style={{ color: "var(--ds-color-ink)" }}>{ev.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                      {ev.time ? new Date(ev.time).toLocaleString() : ""}
                      {ev.location ? ` · ${ev.location}` : ""}
                    </div>
                  </li>
                ))}
                {(!detail.tracking_events || detail.tracking_events.length === 0) && (
                  <li className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>
                    No events yet.
                  </li>
                )}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div
        className="break-all text-sm"
        style={{ color: "var(--ds-color-ink)" }}
      >
        {value}
      </div>
    </div>
  );
}
